/**
 * 发票模板预览用的模拟数据
 * 接口 /invoiceTemplate/{id} 只返回部分字段，预览时缺少的字段从此处取模拟数据
 */
import { multiply, addition, toFixedDecimal } from "@/utils/count";

// 共用的收票方信息
const mockBillTo = {
  name: "SAMPLE CUSTOMER PTE. LTD.",
  address: "123 Sample Road",
  country: "Singapore",
  postalCode: "000000",
};

// 共用的产品列表
const mockProductList = [
  {
    productType: "service",
    serviceInfo: {
      description: "sample Service Charge",
      serviceFee: "1000.00",
      thirdPartyFee: "0.00",
      thirdPartyFeeDescription: "sample third party fee",
      otherFee: "0.00",
      otherFeeDescription: "sample Other Fee",
      currency: "USD",
    },
  },
];

// invoiceType == 1（普通发票）的模拟数据
const mockDataType1 = {
  invoiceNumber: "-02604293682004",
  businessNo: "a000001",
  businessType: "COMPANY_INCORPORATION",
  issuedDate: "2026-04-17 11:11:50",
  dueDate: "2026-05-17 11:11:50",
  // 【参与计算】taxableAmount 作为被乘数，与 supplier.taxInfos[].taxRate 相乘得到各税项 taxAmount
  taxableAmount: "1000.00",
  // 【参与计算】subtotalAmount 作为加数，与 taxAmountSum 相加得到 totalAmount
  subtotalAmount: "1000.00",
  // 【计算字段】totalAmount = subtotalAmount + Σ(taxAmount)，初始值为 null，由 buildInvoiceDetail 计算后赋值
  totalAmount: null,
  deductionAcraFee: null,
  deductionHandlingFee: null,
};

// invoiceType == 2（退款发票）的模拟数据
const mockDataType2 = {
  invoiceNumber: "-02604293682004",
  businessNo: "a000001",
  businessType: "COMPANY_INCORPORATION",
  issuedDate: "2026-04-29 17:56:50",
  dueDate: null,
  // 【参与计算】taxableAmount 作为被乘数，与 supplier.taxInfos[].taxRate 相乘得到各税项 taxAmount
  taxableAmount: "1000.00",
  // 【参与计算】subtotalAmount 作为加数，与 taxAmountSum 相加得到 totalAmount
  subtotalAmount: "1000.00",
  // 【计算字段】totalAmount = subtotalAmount + Σ(taxAmount)，初始值为 null，由 buildInvoiceDetail 计算后赋值
  totalAmount: null,
  deductionAcraFee: "0.00",
  deductionHandlingFee: "0.00",
};

/**
 * 根据 invoiceType 获取对应的模拟数据
 * @param {number|string} invoiceType 1=普通发票 2=退款发票
 * @returns {object} 模拟数据对象
 */
function getMockDataByInvoiceType(invoiceType) {
  return Number(invoiceType) === 2 ? mockDataType2 : mockDataType1;
}

/**
 * 获取共用的收票方模拟数据
 * @returns {object} billTo 对象
 */
export function getMockBillTo() {
  return mockBillTo;
}

/**
 * 获取共用的产品列表模拟数据
 * @returns {array} productList 数组
 */
export function getMockProductList() {
  return mockProductList;
}

/**
 * 组装发票模板预览所需的完整 invoiceDetail 对象
 *
 * 计算字段说明：
 * - taxInfos[].taxAmount： taxableAmount * taxRate，四舍五入保留两位小数（使用 count.js 避免精度丢失）
 * - totalAmount： subtotalAmount + taxInfos 中 taxAmount 累计和(taxAmountSum)，四舍五入保留两位小数
 *
 * 参与计算的字段：
 * - taxableAmount（mockData）：作为被乘数参与 taxAmount 计算
 * - taxRate（supplier.taxInfos 中每项）：作为乘数参与 taxAmount 计算
 * - taxAmount（taxInfos 中每项，计算结果）：累加得到 taxAmountSum
 * - taxAmountSum：作为加数参与 totalAmount 计算
 * - subtotalAmount（mockData）：作为加数参与 totalAmount 计算
 *
 * @param {object} resData 接口 /invoiceTemplate/{id} 返回的 data 对象
 * @returns {object} 组装好的 invoiceDetail 对象
 */
export function buildInvoiceDetail(resData) {
  const data = resData || {};
  const supplier = data.supplier || {};
  const bankAccountList = data.bankAccountList || [];
  const invoiceType = data.invoiceType;
  const mockData = getMockDataByInvoiceType(invoiceType);
  // 【参与计算】taxableAmount：作为被乘数，用于计算各税项 taxAmount
  const taxableAmount = mockData.taxableAmount;
  // 【参与计算】subtotalAmount：作为加数，用于计算 totalAmount
  const subtotalAmount = mockData.subtotalAmount;

  // 【计算字段】taxAmount = taxableAmount * taxRate，四舍五入保留两位小数
  // - taxableAmount：参与计算的应税金额
  // - item.taxRate：参与计算的税率（来自 supplier.taxInfos）
  const taxInfos = (supplier.taxInfos || []).map(item => ({
    taxType: item.taxType,
    taxRate: item.taxRate,
    taxAmount: toFixedDecimal(multiply(taxableAmount, item.taxRate)),
  }));

  // 【计算字段】taxAmountSum = Σ(taxInfos[].taxAmount)，参与 totalAmount 计算
  const taxAmountSum = addition(...taxInfos.map(item => item.taxAmount));

  // 【计算字段】totalAmount = subtotalAmount + taxAmountSum，四舍五入保留两位小数
  // - subtotalAmount：参与计算的应税小计
  // - taxAmountSum：参与计算的税额累计和
  const totalAmount = toFixedDecimal(addition(subtotalAmount, taxAmountSum));

  return {
    invoiceType: invoiceType,
    bankAccountList: bankAccountList,
    billFrom: {
      companyLogoUrl: supplier.companyLogoUrl,
      abbr: supplier.abbr,
      name: supplier.companyName,
      registrationNumber: supplier.registrationNo,
      address: supplier.billingAddress,
      country: supplier.country,
      postalCode: supplier.postCode,
    },
    billingCurrency: bankAccountList[0]?.billingCurrency || "",
    taxInfos: taxInfos,
    invoiceTemplate: {
      country: data.country,
      languageOption: data.languageOption,
    },
    invoiceNumber: `${supplier.invoiceNumberPrefix || ""}${mockData.invoiceNumber}`,
    businessNo: mockData.businessNo,
    businessType: mockData.businessType,
    issuedDate: mockData.issuedDate,
    dueDate: mockData.dueDate,
    taxableAmount: taxableAmount,
    subtotalAmount: subtotalAmount,
    totalAmount: totalAmount,
    deductionAcraFee: mockData.deductionAcraFee,
    deductionHandlingFee: mockData.deductionHandlingFee,
    billTo: getMockBillTo(),
    productList: getMockProductList(),
  };
}
